<template>
  <div>
    <VModal v-if="!!modalOpen" />
    <MessagePrompt v-if="getPromptText !== null" />
    <div
      id="cruz-app"
      class="flex flex-col relative"
    >
      <!-- Update available banner -->
      <Transition name="slide-down">
        <div v-if="updateAvailable" class="cc-update-banner">
          <div class="cc-update-banner__content">
            <font-awesome-icon icon="fa-solid fa-rotate" class="cc-update-banner__icon" />
            <span>A new version of CruzControl is available.</span>
            <button class="cc-update-banner__btn" @click="reloadApp">
              Update Now
            </button>
            <button class="cc-update-banner__dismiss" @click="updateAvailable = false">
              &times;
            </button>
          </div>
        </div>
      </Transition>
      <SystemBanner />
      <CruzHeader v-if="(!!path && !!getUserId && getEmployeeSites.length > 0 || !!longLoad) && path !== '/dev-login'" />
      <div
        v-if="(!!path && !!getUserId && getEmployeeSites.length > 0 && !isInitError && !cognitoError) || noSiteAccess || path === '/dev-login'"
        :class="path !== '/dev-login' && 'underHeaderHeight top-12'"
      >
        <AlertsCorner v-if="path !== '/sitemap' && path !== '/Alerts' && getActiveSite !== null" />
        <SideMenu v-if="path !== '/sitemap' && getActiveSite !== null && !!hasModules" />
        <router-view v-slot="{ Component }" v-if="!!getHeadersSiteId || path === '/sitemap' || noSiteAccess || path === '/dev-login'">
          <component
            ref="routerComponent"
            :is="Component"
            :class="viewClass"
          />
        </router-view>
        <MiniChatManager v-if="path !== '/sitemap' && getActiveSite !== null" />
      </div>
      <!-- LOGO WHILE LOADING -->
      <AppLoadingIndicator
        v-else
        :long-load="longLoad"
        :is-init-error="isInitError"
        :cognito-error="cognitoError"
        @retry="checkUserIdValidity"
      />      
    </div>
  </div>
</template>

<script>
import { mapGetters, mapState, mapActions, mapMutations } from "vuex"
import CruzHeader from "./components/Header/CruzHeader.vue"
import SideMenu from "./components/SideMenu/SideMenu.vue"
import VModal from "./components/Modals/VModal.vue"
import MessagePrompt from "./components/Prompts/MessagePrompt.vue"
import AlertsCorner from "./components/Alerts/AlertsCorner.vue"
import AppLoadingIndicator from "./components/Loading/AppLoadingIndicator.vue"
import MiniChatManager from "./components/MiniChatMessages/MiniChatManager.vue"
import { fetchUserAttributes, signOut } from "@aws-amplify/auth"
import { getSiteModules } from "../Site-Command/services/SiteService"
import { getUser, getEmployeePermissions, getEmployeeSite, getEmployeesList } from "@/services/user.service"
import { getImagesBySource } from "@/services/images.service"
import { getAlerts } from "@/services/alert.services"
import { getSiteTrackPreload } from "../Site-Track/services/SiteData"
import { getRegionsTreeQuery, getRegionsTreeUserQuery, getRegionsTreeQueryForUserManagement } from "@/services/regions.service"
import { getControllerStatus } from '../Site-Command/services/SiteService'
import { connectSocket, joinSite, leaveSite, disconnectSocket } from '@/services/socket.service'
import { getActiveAnnouncements } from '@/services/announcement.service'
import { startVersionCheck, stopVersionCheck } from '@/utils/versionCheck'
import SystemBanner from '@/components/Announcements/SystemBanner.vue'
import Cookies from "js-cookie"
import sls from "@/store/storage"

export default {
  name: "App",
  components: {
    CruzHeader,
    SideMenu,
    VModal,
    MessagePrompt,
    AlertsCorner,
    AppLoadingIndicator,
    SystemBanner,
    // MessageChatBody,
    MiniChatManager
  },
  data() {
    return {
      path: null,
      longLoad: false,
      isInitError: false,
      cognitoError: false,
      siteModules: [],
      lastScrollPosition: 0,
      firstAlertsLoaded: false,
      updateAvailable: false,
      miniMessageText: '',
      isMiniDismissed: false,
      sessionInterval: null
    };
  },
  computed: {
    ...mapState([
      "screen",
      "orientation",
      "canScrollOnBody",
      "active_site",
      "noSiteAccess",
      "headers",
      "accessToken",
      "idToken",
      "sessionTimestamp",
    ]),
    ...mapGetters([
      "modalOpen",
      "currentModal",
      "getPromptText",
      "mobileSideMenuOpen",
      "getUserId",
      "getUser",
      "getActiveTenantId",
      "getActiveSite",
      "getSiteId",
      "getEmployeeSites",
      "getInitError",
      "mainMenuCollapsed",
      "sectionMenuCollapsed",
      "getHeadersSiteId",
      "getEmail",
      "getPermissionSet",
      "getUserPermissions",
      "getAvailableModules",
      "getUserManagementAccess",
      "getIsTenantSuperuser",
      "getAlerts",
      "isControllerStatusBarOpen",
      "getTheme",
      "isSocketConnected",
    ]),
    routePath() {
      return this.$route.path
    },
    isMobile() {
      return /Mobi/i.test(window.navigator.userAgent)
    },
    viewClass() {
      let classes = ["h-full",  "transition-all", "relative"]

      if (this.path !== "/dev-login") classes.push("sectionMinHeight", "mt-12")
      
      if (this.path === "/sitemap" || !this.hasModules || this.noSiteAccess || this.path === "/dev-login") {
        classes.push("w-full", "lg:ml-0")
      } else if (this.noSubSideMenu) {
        if (this.mainMenuCollapsed) {
          classes.push("section-width-expand", "lg:ml-16")
        } else {
          classes.push("section-width", "lg:ml-52")
        }
      } else if (this.mainMenuCollapsed && this.sectionMenuCollapsed) {
        classes.push("section-width-both-collapsed")
      } else if (this.mainMenuCollapsed && !this.sectionMenuCollapsed) {
        classes.push("section-width-main-collapsed")
      } else if (!this.mainMenuCollapsed && this.sectionMenuCollapsed) {
        classes.push("section-width-sub-collapsed")
      } else {
        classes.push("section-width-none-collapsed")
      }
      
      return classes
    },
    noSubSideMenu() {
      return this.path === "/"
        || this.path === "/404"
        || this.path === "/Alerts"
        || this.path === "/Releases"
        || this.path === "/support"
        || this.path === "/settings"
        || this.path === "/messages"
        || this.path === "/noSite"
    },
    hasModules() {
      return this.getAvailableModules?.length > 0
    },
    hasRegionsReadAccess() {
      if (this.getUserManagementAccess || this.getIsTenantSuperuser) return true
      return this.getUserPermissions?.configuration?.regions?.regions?.main_view?.object?.read_bool
    },
    alertsReadAccess() {
      if (this.getIsTenantSuperuser) return true
      return this.getUserPermissions?.alerts?.alerts?.alerts?.main_view?.object?.read_bool
    },
    isOfflineDev() {
      return import.meta.env.VITE_OFFLINE_DEV === "true"
    },
    isScreenLg() {
      return this.screen.lg
    },
  },
  watch: {
    routePath(val, oldVal) {
      if (val !== oldVal && this.path) this.path = val
    },
    getSiteId(val, oldVal) {
      if (val && val !== oldVal) {
        // Leave old site's socket room, join new one
        if (oldVal) leaveSite(oldVal)
        if (val) joinSite(val)
        this.$store.commit('resetSocketState')
        this.validateUserPermissions(val)
        this.firstAlertsLoaded = false
        this.resetNotifications()
        this.loadAlerts()
        this.loadSiteData()

        // Reload announcements for the new site
        getActiveAnnouncements(val).then(response => {
          if (response?.status === 200 && response.data) {
            this.$store.commit('setAnnouncements', response.data)
          }
        }).catch(err => console.warn('[Announcements] Failed to load on site switch:', err))
      }
    },
    canScrollOnBody(val) {
      if (val === false) document.querySelector("body").style.overflowY = "hidden"
      else document.querySelector("body").style.overflowY = "auto"
    },
    getInitError(val) {
      if (val) this.isInitError = true
      else this.isInitError = false
    },
    hasRegionsReadAccess(val, oldVal) {
      if (val && !oldVal) this.loadRegions()
    },
    getUserId(val, oldVal) {
      if (val && val !== oldVal) {
        // Keep chat identity in sync in every tab so echoed own messages
        // are not counted as unread when MessagesPage has not mounted.
        this.setChatCurrentUserId(val)
        this.checkUserIdValidity()
      }
    },
  },
  beforeMount() {
    this.loadAlerts()
    this.checkUserIdValidity()
  },
  async mounted() {
    // Apply persisted theme on startup
    console.log("theme on mount", this.getTheme)
    document.documentElement.setAttribute('data-theme', this.getTheme || 'light')
    this.setSessionTimestamp(new Date())
    this.closeModal()
    this.closePrompt()
    this.handleResize()
    this.setOrientation(!window.matchMedia("(orientation: portrait)").matches)
    document.querySelector("body").style.overflowY = "auto"
    addEventListener("resize", this.handleResize)
    addEventListener("scroll", this.handleScroll)
    addEventListener("orientationchange", this.handleOrientationChange)

    // Start version polling (check every 5 minutes)
    startVersionCheck(() => {
      this.updateAvailable = true
    })

    await this.$router.isReady()
    .then(() => {
      this.path = this.$route.path
    })
    .catch(() => this.path = this.$route.path)
    setTimeout(() => {
      this.longLoad = true
    }, 2000)
    // Check session validity every 60 seconds
    this.sessionInterval = setInterval(this.checkSessionStatus, 60000)
    // Alert polling disabled — alerts now arrive in real-time via Socket.IO (RabbitMQ → backend → socket).
    // If socket proves unreliable, re-enable with a longer interval (e.g. 5 min) as a safety net.
    // this.alertsInterval = setInterval(this.loadAlerts, 60000)
  },
  beforeUnmount() {
    removeEventListener("resize", this.handleResize)
    removeEventListener("scroll", this.handleScroll)
    removeEventListener("orientationchange", this.handleOrientationChange)
    // clearInterval(this.pingInterval)
    // clearInterval(this.alertsInterval)
    clearInterval(this.sessionInterval)
    disconnectSocket()
    stopVersionCheck()
  },
  methods: {
    ...mapActions([
      "setSTData",
    ]),
    ...mapMutations([
      "closeModal",
      "closePrompt",
      "LogOutAT",
      "setPrompt",
      "setScreenSize",
      "setOrientation",
      "setRouteBeforeForcedLogout",
      "setCurrentSessionData",
      "setEmployeeSites",
      "setActiveSite",
      "setPermissionSet",
      "setAvailableModules",
      "setEmployeePermissions",
      "setGlobalRegion",
      "SELECT_SITE",
      // "setEmployeeId",
      // "setSiteId",
      "setUser",
      "setUserObject",
      "setUserTenantAccess",
      "setNoSiteAccess",
      "setUserAvatar",
      "setPOS_System",
      "setAlerts",
      "setAlertsFromRefresh",
      "setSideMenuNotifications",
      "resetNotifications",
      "showModuleHeader",
      "setAtTopOfPage",
      "setScrollTop",
      "setSessionTimestamp",
      "resetFromPortalBool",
      "resetSocketState",
      "setChatCurrentUserId"
    ]),
    reloadApp() {
      window.location.reload()
    },
    async checkUserIdValidity() {

      // new boolean set from portal login indicates no active site
      if (this.getUser?.from_portal === true) {
        console.log("from portal")
        localStorage.removeItem("cruzcontrolSite")
        sls.remove("cruzcontrolSite");
        this.setActiveSite(null)
        this.$router.push("/sitemap")
        this.resetFromPortalBool()
      }

      if (import.meta.env.VITE_DEV_NETWORK_ACCESS !== "true") this.loadEmployeeAvatar()
      if (import.meta.env.VUE_APP_OFFLINE_DEV !== "true") {
        // Blank Cognito Data
        let cognitoData
        try {
          const attributes = await fetchUserAttributes();
          console.log({ attributes })
          // debugger
          cognitoData = { attributes };
        }
        catch (error) {
          console.error(error)
          this.cognitoError = true
          return
        }

        // Check with Portal API to get our real user
        let user
        await getUser(cognitoData.attributes.email)
        .then(response => {
          console.log(response)
          if (response.status == 200 && Array.isArray(response.data) && response.data.length > 0) {
            console.log(response.data[0])
            user = response.data[0]
          }
          else {
            console.error(response)
            this.setPrompt(["Unable to verify user information from server.", "closePrompt", null, { header: "Error" }])
            this.cognitoError = true
            return
          }
        })
        // console.log({ user })
        // Verify we are who we say we are as a client.
        if (user.id === this.getUserId) {
          // Load Employee list from Portal. Employee is a 1:1 user to site representation
          let myEmployees
          await getEmployeesList(user.id)
          .then(response => {
            myEmployees = response?.data?.results
            console.log(myEmployees)
          })
          this.loadRegions()
          if (import.meta.env.VITE_DEV_NETWORK_ACCESS === "true") this.loadEmployeeAvatar()
          // If state.active_site is null, set defaults.
          // console.log(myEmployees[0])
          if (myEmployees[0]) {
            this.setNoSiteAccess(false)
            if (this.getActiveSite?.site_id) {
              let currentSite = myEmployees.filter(site => site.site_id === this.getActiveSite.site_id)[0]
              if (!currentSite) {
                this.$router.push("/sitemap")
                return
              }
              // WASHLINK - will have to change to site config
              this.setPOS_System(currentSite.pos_system)
              this.setActiveSite(currentSite)
              // this.setSiteId(currentSite.site_id)
              // this.setEmployeeId(currentSite.id)
              this.setPermissionSet(currentSite.permissions)
              this.loadSiteData()
              await this.loadSiteModules()
              this.readyPermissions()
              this.loadAlerts()
            } 
            // else {
            //   this.setActiveSite(myEmployees[0]);
            //   this.setPermissionSet(myEmployees[0].permissions)
            //   this.setEmployeeId(myEmployees[0].id)
            //   this.setSiteId(myEmployees[0].site_id)
            // }
            else {
              this.setActiveSite(myEmployees[0])
              this.setPermissionSet(myEmployees[0].permissions)
              // this.setEmployeeId(myEmployees[0].id)
              this.setPOS_System(myEmployees[0].pos_system)
            }
            if (this.getActiveSite === null) {
              await this.$router.push("/sitemap")
            }
            // set a global state for employee sites.
            this.setEmployeeSites(myEmployees)

            // Initialize Socket.io connection after successful auth
            if (this.idToken) {
              if (this.getUserId) this.setChatCurrentUserId(this.getUserId)
              connectSocket(this.idToken, this.$store)
              if (this.getSiteId) {
                joinSite(this.getSiteId)
              }
            }

            // Fetch active system announcements
            if (this.getSiteId) {
              getActiveAnnouncements(this.getSiteId).then(response => {
                if (response?.status === 200 && response.data) {
                  this.$store.commit('setAnnouncements', response.data)
                }
              }).catch(err => console.warn('[Announcements] Failed to load:', err))
            }
          }
          else {
            await this.$router.push("/noSite")
            this.setNoSiteAccess(true)
          }
        }
        // User ID does not match Login ID.. They tried to do a bad thing.
        else {
          console.warn("User ID does not match Login ID!", { user, getUserId: this.getUserId })
        }
      }
      // else load sample data for offline dev purposes
      else {
        console.log("setting offline")
        let myEmployees = currentSiteSampleData
        this.setActiveSite(myEmployees[0])
        this.setPermissionSet(myEmployees[0].permissions)
        // this.setEmployeeId(myEmployees[0].id)
        // this.setSiteId(myEmployees[0].site_id)
        this.setPOS_System(myEmployees[0].pos_system)
        this.setEmployeeSites(myEmployees)
        this.setUser(sampleUser)
        this.setPermissionSet(myEmployees[0].permissions)
        this.loadSiteData()
        await this.loadSiteModules()
        this.readyPermissions()
        this.loadAlerts()
      }
    },
    async checkSessionStatus() {
      if (!this.sessionTimestamp) return

      const timeSinceLastActivity = new Date() - new Date(this.sessionTimestamp)
      const ONE_HOUR = 3600000

      if (timeSinceLastActivity > ONE_HOUR) {
        console.log("Inactivity timeout exceeded. Logging out.")
        // debugger
        this.executeLogout()
        return
      }
    },
    executeLogout() {
      this.setActiveSite(null)
      this.setSessionTimestamp(null)
      this.LogOutAT()
      Cookies.remove("cruzcontrol")
      Cookies.remove("cruzcontrolSite")
      sls.remove("cruzcontrolSite")
      window.location.href = "https://portal.cruzcontrol.app/inactivity"
    },
    loadSiteModules() {
      return new Promise(resolve => {
        getSiteModules(this.headers)
        .then(response => {
          if (response.status === 200 && response.data && !Object.keys(response.data).includes("error")) {
            this.siteModules = response.data[0]?.config_data?.modules
            //this.setPOS_System(response.data[0]?.config_data?.pos_system)
          }
          else {
            console.log(response)
          }
          resolve(response.status)
        }).catch(error => {
          console.log(error)
          resolve(null)
        })
      })
    },
    loadRegions() {
      return new Promise(resolve => {
        const endpoint = this.hasRegionsReadAccess
          ? (this.getUserManagementAccess
            ? getRegionsTreeQueryForUserManagement({ region_id: "e7303baf-81ef-4835-8bd8-6b3a61a49f4a" }, this.headers)
            : getRegionsTreeQuery({ region_id: "e7303baf-81ef-4835-8bd8-6b3a61a49f4a" }, this.headers))
          : getRegionsTreeUserQuery({ user_id: this.getUserId }, this.headers)
        endpoint.then(response => {
          if (response.status === 200 && response.data && !Object.keys(response.data).includes("error")) {
            console.log(response)
            this.setGlobalRegion(response.data.results)
          }
          else {
            console.log(response)
          }
          resolve(response.status)
        }).catch(error => {
          console.log(error)
          resolve(null)
        })
      })
    },
    async readyPermissions() {
      let permissionsResponse = []
      await getEmployeePermissions(this.headers)
      .then(response => {
        if (response.user_data) {
          this.setUserTenantAccess(response.user_data)
          // this.setUserTenantAccess({
          //   "active": true,
          //   "is_tenant_superuser": false,
          //   "user_management": true,
          // })
        }
        if (response.results) permissionsResponse = response.results
        else {
          console.log(response)
          this.setPrompt(["Unable to retrieve your user permissions.", "closePrompt", null, { header: "Error" }])
          return
        }
      }).catch(error => {
        console.log(error)
        this.setPrompt(["Unable to retrieve your user permissions.", "closePrompt", null, { header: "Error" }])
        return
      })

      let modules = {}
      let availableModules = []

      for (let config of permissionsResponse) {
        // create module keys
        if (!modules[config.module]) modules[config.module] = {}
        // inside of that, create feature keys
        if (!modules[config.module][config.feature]) modules[config.module][config.feature] = {}
        // inside of that, create view keys
        if (!modules[config.module][config.feature][config.view]) modules[config.module][config.feature][config.view] = {}
        // inside of that, create object keys
        if (!modules[config.module][config.feature][config.view][config.object]) modules[config.module][config.feature][config.view][config.object] = {}
        // inside of that, create optional sub-object key
        if (!config.sub_object) {
          modules[config.module][config.feature][config.view][config.object].final = config
        }
        else {
          modules[config.module][config.feature][config.view][config.object][config.sub_object] = config
        }
        // if config has a main_view whose read_bool is set to true, the user has access to that module
        if (config.object === "main_view" && config.read_bool && !availableModules.includes(config.module)
          &&
          // a user can have FEWER than the site, but never more
          this.siteModules.includes(config.module)
        ) {
          availableModules.push(config.module)
        }
      }

      // Configuration module should always be available if user has user management access
      if (this.getUserManagementAccess) availableModules.push("configuration")

      // Administration module is superuser-only
      if (this.getIsTenantSuperuser) availableModules.push("administration")

      this.setAvailableModules(availableModules)
      if (availableModules.length === 0) {
        this.setPrompt(["You have not been granted permissions to any site modules. You should contact your administrator to inquire about access.", "closePrompt", null, { header: "Warning" }])
      }
      await this.setEmployeePermissions(modules)
      return
    },
    async validateUserPermissions(site_id){
      if (import.meta.env.VITE_OFFLINE_DEV === "true") return
      let cognitoData;
      
      try {
        cognitoData = await fetchUserAttributes();
        console.log(cognitoData);
      } catch (error) {
        console.error("Error fetching user:", error);
        return null;
      }
      let user
      await getUser(cognitoData.email)
      .then(response => {
        if (response.status == 200 && Array.isArray(response.data) && response.data.length > 0) {
          user = response.data[0]
        }
        else {
          console.error(response)
          this.setPrompt(["Unable to verify user information from server.", "closePrompt", null, { header: "Error" }])
          this.cognitoError = true
          return
        }
      })
      if (user.id == this.getUserId){
        let mySite = (await getEmployeeSite(user.id, site_id))?.data.results;
        console.log({mySite});
        await this.loadSiteModules()
        this.setPermissionSet(mySite[0]?.permissions)
        this.readyPermissions()
      }
      else{
        //console.log("Logging user data")
        console.log(user);
        console.log(cognitoData)
      }
    },
    loadSiteData() {
      getSiteTrackPreload(this.headers)
      .then((response) => {
        if (response?.status === 200) {
          this.setSTData(response.data)
        }
        else {
          console.log(response)
          this.setPrompt(["Could not load all data for this site!", "loadSiteData", null, { header: "Error" }])
        }
      }, (error) => {
        console.log(error)
        this.setPrompt(["Could not load all data for this site!", "loadSiteData", null, { header: "Error" }])
      })
    },
    loadEmployeeAvatar() {
      getImagesBySource(this.getUserId, this.headers)
      .then(response => {
        if (response.status === 200) this.setUserAvatar(response.data?.results?.[0])
        else console.log(response)
      }).catch(error => console.log(error))
    },
    async loadAlerts() {

      // using alerts ping to also periodically check that ID token is still valid
      let expired = false
      
      // check if one hour since last timestamp
      // console.log("session timestamp when loading alerts:", this.sessionTimestamp != null ? new Date(this.sessionTimestamp) : this.sessionTimestamp)
      // console.log("new Date:", new Date())
      // console.log("delta:", this.sessionTimestamp ? new Date() - new Date(this.sessionTimestamp) : null)
      if (this.sessionTimestamp != null && new Date() - new Date(this.sessionTimestamp) > 3600000) {
        console.log("expired!")
        expired = true
      }

      // Skip HTTP polling if Socket.io is connected (real-time alerts active)
      // Still check session expiry above, but don't make redundant HTTP calls
      else if (this.isSocketConnected && this.firstAlertsLoaded) {
        return
      }

      // can't use alerts endpoint if on sitemap (no active site)
      else if (this.active_site) {
        // do not fetch access token and ID token from Auth.currentSession()
        // as that will always refresh the tokens
        // and this is meant to be a passive call
        const headers = {
          ...this.headers,
          'accesstoken': this.accessToken,
          'idtoken': this.idToken,
        }
  
        // alerts are permissions-based
        // so use other (controller status) endpoint if no read access
        let endpoint = getAlerts(this.active_site.site_id, headers)
        if (!this.alertsReadAccess) {
          endpoint = getControllerStatus(headers)
          // if controller status bar is open, status already pinged there
          if (this.isControllerStatusBarOpen) return
        }

        await endpoint.then(response => {
          if (response.status === 200) {
            // set alerts if have read access
            if (this.alertsReadAccess) {
              let alerts = response.data.results
              if (this.firstAlertsLoaded) {
                const existingIds = new Set(this.getAlerts.map(a => a.id))
                const newFromRefresh = alerts.filter(a => !existingIds.has(a.id))
                this.setAlertsFromRefresh(newFromRefresh)
              }
              else this.setAlerts(alerts)
              let numNewAlerts = alerts.filter(x => !x.acknowledged).length
              this.setSideMenuNotifications(["alerts", { count: numNewAlerts }])
              this.firstAlertsLoaded = true
            }
          }
          else {
            console.log(response)
            // check for 401 Unauthorized error
            if (response.status === 401) {
              if (response.message === "jwt expired") {
                // if expired but within one hour of last activity, can refresh
                if (this.sessionTimestamp != null && new Date() - new Date(this.sessionTimestamp) < 3600000) {
                  console.log("token expired but can refresh!")
                }
                // else if more than one hour since last activity, must log out
                else {
                  console.log("token expired and cannot refresh!")
                  expired = true
                }
              }
              // other reasons for 401 error, log out as well
              else if (response.message === "Refresh Token has expired"
                || response.message === "invalid signature"
                || response.message === "Access Token has expired"
                || response.message === "ID Token has expired"
                || response.message === "ID Token missing from header"
              ) {
                console.log("expired!")
                expired = true
              }
            }
            else {
              if (this.alertsReadAccess) this.setPrompt(["Unable to load alerts for this site.", "loadAlerts", null, { header: "Error" }])
            }
          }
        }).catch(err => {
          console.log(err)
          // if error message includes unauthorized reasons, log out as well
          if (err.message === "jwt expired") {
            if (this.sessionTimestamp != null && new Date() - new Date(this.sessionTimestamp) < 3600000) {
              console.log("token expired but can refresh!")
            }
            else {
              console.log("token expired and cannot refresh!")
              expired = true
            }
          }
          else if (err.message === "Refresh Token has expired"
            || err.message === "invalid signature"
            || err.message === "Access Token has expired"
            || err.message === "ID Token has expired"
            || err.message === "ID Token missing from header"
          ) {
              console.log("expired!")
              expired = true
            }
        });
      }

      if (expired) {
        console.log("Logging out due to expired token")
        this.setActiveSite(null)
        this.setSessionTimestamp(null)
        this.LogOutAT()
        if (import.meta.env.VITE_DEV_NETWORK_ACCESS === "true") {
          try {
            signOut().then(() => {
              this.$router.push("/dev-login");
              Cookies.remove("cruzcontrol")
              Cookies.remove("cruzcontrolSite");
              sls.remove("cruzcontrolSite");
            });
          } catch (error) {
            console.error('error signing out: ', error);
          }
        }
        else {
          Cookies.remove("cruzcontrol")
          Cookies.remove("cruzcontrolSite");
          sls.remove("cruzcontrolSite");
          window.location.href = "https://portal.cruzcontrol.app/logout"
        }
      }
    },
    handleResize() {
      this.setScreenSize(window.innerWidth)
    },
    handleOrientationChange() {
      this.setOrientation(window.matchMedia("(orientation: portrait)").matches)
    },
    handleScroll() {
      const scrollTop = window.scrollY
      this.setScrollTop(scrollTop)
      this.setAtTopOfPage(scrollTop > 0 ? false : true)
      if (scrollTop < 0) return
      if (Math.abs(scrollTop - this.lastScrollPosition) < 60) return
      this.showModuleHeader(scrollTop < this.lastScrollPosition)
      this.lastScrollPosition = scrollTop
    },
  },
}
</script>